import { toast } from "@coval/ui"

import {
  useAgentMutationsTransportAll,
  useAgentMutationsTransportList,
  useAgentMutationTransportActions,
  type AgentMutationsAllData,
  type AgentMutationsListData,
} from "../transport/useAgentMutationsTransport"
import type { AgentMutation } from "../types"

function parseErrorMessage(message: string): string {
  if (message.includes("Override keys not found")) {
    const match = message.match(
      /Override keys not found on parent agent: \[(.*?)\]/,
    )
    if (match) {
      const keys = match[1]
        .replace(/'/g, "")
        .split(", ")
        .map((k) => k.trim())
        .filter(Boolean)
      if (keys.length > 0) {
        const keyList = keys.slice(0, 3).join(", ")
        const more = keys.length > 3 ? ` and ${keys.length - 3} more` : ""
        return `Cannot override keys that don't exist on the agent: ${keyList}${more}. Only modify values for existing configuration keys.`
      }
    }
    return "You can only override configuration keys that already exist on the agent."
  }

  if (message.startsWith("Validation failed:")) {
    try {
      const jsonPart = message.replace("Validation failed: ", "")
      const parsed: unknown = JSON.parse(jsonPart)
      if (Array.isArray(parsed)) {
        const first = parsed[0] as Record<string, unknown> | undefined
        if (typeof first?.description === "string") {
          return parseErrorMessage(first.description)
        }
      }
    } catch {
      // Not valid JSON, return as-is
    }
  }

  return message
}

function getTransportErrorMessage(error: {
  detail?: { message?: string }
  message: string
}) {
  return error.detail?.message ?? error.message
}

interface UseAgentMutationsListOptions<T> {
  enabled?: boolean
  select?: (data: AgentMutationsListData) => T
}

export function useAgentMutationsList<T = AgentMutationsListData>(
  agentId: string,
  options?: UseAgentMutationsListOptions<T>,
) {
  return useAgentMutationsTransportList(agentId, options)
}

interface UseAgentMutationsAllOptions<T> {
  enabled?: boolean
  select?: (data: AgentMutationsAllData) => T
}

export function useAgentMutationsAll<T = AgentMutationsAllData>(
  options?: UseAgentMutationsAllOptions<T>,
) {
  return useAgentMutationsTransportAll(options)
}

interface UseAgentMutationActionsOptions {
  agentId: string
  enabled?: boolean
}

export function useAgentMutationActions({
  agentId,
  enabled = true,
}: UseAgentMutationActionsOptions) {
  const {
    data: mutations,
    isLoading,
    error,
    refetch,
  } = useAgentMutationsList(agentId, { enabled: enabled && !!agentId })

  const mutationActions = useAgentMutationTransportActions({
    agentId,
    onCreateSuccess: () => {
      toast.success({
        title: "Mutation created",
        description: "The mutation has been created successfully.",
      })
    },
    onCreateError: (error) => {
      toast.danger({
        title: "Failed to create mutation",
        description: parseErrorMessage(getTransportErrorMessage(error)),
      })
    },
    onUpdateSuccess: () => {
      toast.success({
        title: "Mutation updated",
        description: "The mutation has been updated successfully.",
      })
    },
    onUpdateError: (error) => {
      toast.danger({
        title: "Failed to update mutation",
        description: parseErrorMessage(getTransportErrorMessage(error)),
      })
    },
    onArchiveSuccess: () => {
      toast.success({
        title: "Mutation archived",
        description: "The mutation has been archived successfully.",
      })
    },
    onArchiveError: (error) => {
      const message = getTransportErrorMessage(error)
      toast.danger({
        title: "Failed to archive mutation",
        description: parseErrorMessage(message),
      })
    },
  })

  const handleCreate = async (data: {
    display_name: string
    description?: string
    config_overrides: Record<string, unknown>
    parameter_values?: Record<string, string>
  }): Promise<AgentMutation> => {
    return mutationActions.create(data)
  }

  const handleUpdate = async (
    mutationId: string,
    data: {
      display_name?: string
      description?: string
      config_overrides?: Record<string, unknown>
      parameter_values?: Record<string, string>
    },
  ): Promise<AgentMutation> => {
    return mutationActions.update(mutationId, data)
  }

  const handleArchive = async (mutationId: string): Promise<void> => {
    await mutationActions.archive(mutationId)
  }

  return {
    mutations: mutations || [],
    isLoading,
    error,
    refetch,
    create: handleCreate,
    update: handleUpdate,
    archive: handleArchive,
    isCreating: mutationActions.isCreating,
    isUpdating: mutationActions.isUpdating,
    isArchiving: mutationActions.isArchiving,
  }
}
